import { useRef, useState } from 'react';
import ConfirmDialog from './ConfirmDialog';
import useDismiss from './useDismiss';
import { ladderWith, servingsWord } from './servings';

/** "Mark cooked", and the less common "cooked, but not for the number planned".
 *
 * Split rather than a stepper because the ordinary case is one tap and should
 * stay one tap. The week almost always feeds the household it was planned
 * for; a spare tomato turning a dinner for two into a dinner for four is the
 * exception, and an exception belongs behind the caret rather than in front
 * of every dinner on the page.
 *
 * Both routes confirm. Marking cooked cannot be undone -- there is no
 * endpoint for it and no control on the card -- and it empties the pantry of
 * everything the recipe uses, so a mis-tap costs a real correction by hand.
 * That is true of the plain button as much as the menu, which is why the
 * dialog is not reserved for the unusual path.
 */
export default function CookedButton({
  recipeTitle,
  plannedServings,
  pending,
  onCook,
}: {
  recipeTitle: string;
  /** What the week planned for: the left button, and the baseline explained. */
  plannedServings: number;
  pending: boolean;
  onCook: (servings: number) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  // The number waiting to be confirmed, which is also what opens the dialog.
  // Held as the value rather than a boolean so the dialog can say it back.
  const [asking, setAsking] = useState<number | null>(null);
  const group = useRef<HTMLDivElement>(null);

  useDismiss(menuOpen, group, () => setMenuOpen(false));

  // The planned figure is dropped from the menu: the button beside the caret
  // already is that number, and offering it twice invites the reading that
  // the two do different things.
  const others = ladderWith(plannedServings).filter(
    (count) => count !== plannedServings,
  );

  function ask(servings: number) {
    setMenuOpen(false);
    setAsking(servings);
  }

  return (
    <>
      <div className="picker cooked" ref={group}>
        <button
          className="btn btn--small cooked__main"
          disabled={pending}
          onClick={() => ask(plannedServings)}
        >
          Mark cooked
        </button>
        <button
          className="btn btn--small cooked__caret"
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          aria-label={`Mark ${recipeTitle} cooked for a different number of servings`}
          disabled={pending}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span aria-hidden="true">▾</span>
        </button>

        {menuOpen && (
          <ul className="picker__menu" role="menu">
            {others.map((count) => (
              <li key={count} role="none">
                <button role="menuitem" type="button" onClick={() => ask(count)}>
                  Cooked {servingsWord(count)}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {asking !== null && (
        <ConfirmDialog
          titleId="confirm-cooked-title"
          title={`Cook ${servingsWord(asking)} of ${recipeTitle}?`}
          onCancel={() => setAsking(null)}
          actions={
            <>
              <button
                className="btn btn--primary"
                disabled={pending}
                onClick={() => {
                  onCook(asking);
                  setAsking(null);
                }}
              >
                Cooked {servingsWord(asking)}
              </button>
              <button className="link-button" onClick={() => setAsking(null)}>
                Back
              </button>
            </>
          }
        >
          <p className="muted">
            That takes {servingsWord(asking)}' worth of its ingredients out of the pantry
            {asking === plannedServings
              ? ', which is what this week planned for.'
              : `, rather than the ${plannedServings} this week planned for.`}
          </p>
          {/* The reason this asks at all. Nothing on the card undoes it, and
              putting the pantry back means editing jars by hand. */}
          <p className="muted small">This cannot be undone.</p>
        </ConfirmDialog>
      )}
    </>
  );
}
